import { z } from 'zod';

import { optionalText, optionalWebUrl, requiredText } from './fieldSchemas';

/**
 * Company module validation (BR Company #1/#2/#4). Shared between `willnq-web` and (future)
 * `willnq-mobile` so the rules are enforced identically — client-side validation for instant
 * feedback only; the backend re-validates via FluentValidation on receipt
 * (docs/development/FrontendGuidelines.md §8.1). Name/Code uniqueness can only be checked server-side, so it
 * surfaces as a mapped field error from the mutation payload, not here.
 */

/** Company size band — mirrors the backend GraphQL `CompanySize` enum. */
export const COMPANY_SIZE_VALUES = ['STARTUP', 'SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE'] as const;

export const COMPANY_LOGO_MAX_SIZE_BYTES = 2 * 1024 * 1024;
export const COMPANY_LOGO_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'] as const;

/** The admin company form. `stored` is the company being edited (ADR-0038 §4); omit it to create. */
export const companyFormSchemaFor = (stored?: { name?: string | null; website?: string | null; industry?: string | null }) =>
  z.object({
  name: requiredText('Company name', 200, stored?.name),
  code: z
    .string()
    .trim()
    .min(1, 'Company code is required')
    .max(50, 'Code must be 50 characters or fewer')
    .regex(/^[A-Za-z0-9-]+$/, 'Code may contain only letters, numbers and hyphens'),
  description: z.string().max(2000, 'Description must be 2000 characters or fewer').default(''),
  website: optionalWebUrl(stored?.website).default(''),
  industry: optionalText('Industry', 100, stored?.industry).default(''),
  size: z.enum(COMPANY_SIZE_VALUES),
});

export const companyFormSchema = companyFormSchemaFor();

export type CompanyForm = z.infer<typeof companyFormSchema>;
